import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BottomTabBar } from "@/components/BottomTabBar";
import { InstallPrompt } from "@/components/InstallPrompt";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Blogs from "@/pages/Blogs";
import SignIn from "@/pages/SignIn";
import Terms from "@/pages/Terms";
import Franchise from "@/pages/Franchise";
import MyPups from "@/pages/account/MyPups";
import MyBookings from "@/pages/account/MyBookings";
import Book from "@/pages/account/Book";
import Rates from "@/pages/account/Rates";
import Profile from "@/pages/account/Profile";
import Admin from "@/pages/admin/Admin";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !hash.includes("session_id=")) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "instant" });
      }, 400);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    return undefined;
  }, [pathname, hash]);
  return null;
};

const PageLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center pt-32">
    <p className="font-display text-xl text-navy">Loading…</p>
  </div>
);

const Protected = ({ children, admin = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/signin" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/account" replace />;
  if (!admin && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
};

const GuestOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/account"} replace />;
  return children;
};

const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/account/book"} replace />;
  return <Home />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/services" element={<Services />} />
      <Route path="/pricing" element={<Navigate to="/signin" replace />} />
      <Route path="/about" element={<About />} />
      <Route path="/blogs" element={<Blogs />} />
      <Route
        path="/signin"
        element={
          <GuestOnly>
            <SignIn />
          </GuestOnly>
        }
      />
      <Route path="/terms" element={<Terms />} />
      <Route path="/franchise" element={<Franchise />} />
      <Route
        path="/account"
        element={
          <Protected>
            <MyPups />
          </Protected>
        }
      />
      <Route
        path="/account/bookings"
        element={
          <Protected>
            <MyBookings />
          </Protected>
        }
      />
      <Route
        path="/account/book"
        element={
          <Protected>
            <Book />
          </Protected>
        }
      />
      <Route
        path="/account/rates"
        element={
          <Protected>
            <Rates />
          </Protected>
        }
      />
      <Route
        path="/account/profile"
        element={
          <Protected>
            <Profile />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected admin>
            <Admin />
          </Protected>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col bg-cream text-ink">
          <Navbar />
          <main className="flex-1">
            <AppRouter />
          </main>
          <Footer />
          <BottomTabBar />
          <InstallPrompt />
        </div>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
